/**
 * Compact "served-by" indicator: the provider tier that actually handled a
 * request ("vertex", "fal", "openai", …) plus the concrete model id / FAL
 * endpoint. With provider-tier fallbacks this can differ from the requested
 * model, so it surfaces which backend ultimately produced the result. Shared by
 * the Request History rows/cards and the Playground result panel.
 */

// Per-provider tint so the actual serving tier is recognisable at a glance.
const PROVIDER_STYLES: Record<string, string> = {
  vertex: 'bg-indigo-100 text-indigo-700',
  gemini_api: 'bg-violet-100 text-violet-700',
  api_key: 'bg-violet-100 text-violet-700',
  fal: 'bg-amber-100 text-amber-700',
  openai: 'bg-emerald-100 text-emerald-700',
  azure: 'bg-sky-100 text-sky-700',
  local: 'bg-slate-100 text-slate-600',
  dummy: 'bg-slate-100 text-slate-500',
};

// Same palette, one step lighter, for the static "who serves this endpoint"
// pill on the endpoint cards and detail header.
const PROVIDER_PILL_STYLES: Record<string, string> = {
  vertex: 'text-indigo-600 bg-indigo-50',
  gemini_api: 'text-violet-700 bg-violet-50',
  api_key: 'text-violet-700 bg-violet-50',
  fal: 'text-amber-700 bg-amber-50',
  openai: 'text-emerald-700 bg-emerald-50',
  azure: 'text-sky-700 bg-sky-50',
  local: 'text-slate-600 bg-slate-100',
  dummy: 'text-slate-500 bg-slate-100',
};

/**
 * The provider tag on an endpoint card / header. Shows the tier that would
 * actually serve a request now (`active_provider`), which differs from the
 * configured one wherever a fallback chain applies — gpt-image-2 is declared as
 * "openai" but runs on Azure whenever `AZURE_OPENAI_*` is set.
 */
export function ProviderPill({
  provider,
  configuredProvider,
  className = '',
}: {
  provider: string;
  configuredProvider?: string;
  className?: string;
}) {
  const style = PROVIDER_PILL_STYLES[provider] ?? 'text-slate-600 bg-slate-100';
  const differs = Boolean(configuredProvider) && configuredProvider !== provider;
  return (
    <span
      className={`text-[11px] font-medium uppercase tracking-wide rounded-full px-2 py-0.5 ${style} ${className}`}
      title={
        differs
          ? `Active provider tier: ${provider} (configured as ${configuredProvider})`
          : `Provider: ${provider}`
      }
    >
      {provider}
    </span>
  );
}

export function EndpointBadge({
  provider,
  modelId,
  className = '',
}: {
  provider: string | null | undefined;
  modelId: string | null | undefined;
  className?: string;
}) {
  const prov = provider?.trim() || '';
  const model = modelId?.trim() || '';
  if (!prov && !model) return null;
  const style = PROVIDER_STYLES[prov] ?? 'bg-slate-100 text-slate-600';
  const full = prov && model ? `${prov} · ${model}` : prov || model;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-mono max-w-[260px] ${style} ${className}`}
      title={`Served by ${full}`}
    >
      <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.75}
          d="M5 7a2 2 0 012-2h10a2 2 0 012 2v2a2 2 0 01-2 2H7a2 2 0 01-2-2V7zM5 15a2 2 0 012-2h10a2 2 0 012 2v2a2 2 0 01-2 2H7a2 2 0 01-2-2v-2zM8 8h.01M8 16h.01"
        />
      </svg>
      {prov && <span className="font-semibold shrink-0">{prov}</span>}
      {prov && model && <span className="opacity-50 shrink-0">·</span>}
      {model && <span className="truncate">{model}</span>}
    </span>
  );
}
